import { useCallback, useEffect, useMemo, useState } from "react";

export default function useGallery(images = []) {
  const [selectedId, setSelectedId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  // Reset selected image when product changes
  useEffect(() => {
    if (images.length > 0) {
      setSelectedId(images[0].id);
    } else {
      setSelectedId(null);
    }
  }, [images]);

  // Current selected index
  const currentIndex = useMemo(() => {
    if (!images.length || selectedId === null) return -1;

    return images.findIndex((image) => image.id === selectedId);
  }, [images, selectedId]);

  // Selected image
  const selected = useMemo(() => {
    if (currentIndex < 0) return null;

    return images[currentIndex];
  }, [images, currentIndex]);

  // Select image
  const selectImage = useCallback((image) => {
    if (!image) return;

    setSelectedId(image.id);
  }, []);

  // Next image
  const next = useCallback(() => {
    if (images.length === 0) return;

    const nextIndex = (currentIndex + 1) % images.length;

    const nextImage = images[nextIndex];

    if (nextImage) {
      setSelectedId(nextImage.id);
    }
  }, [images, currentIndex]);

  // Previous image
  const previous = useCallback(() => {
    if (images.length === 0) return;

    const prevIndex =
      (currentIndex - 1 + images.length) % images.length;

    const prevImage = images[prevIndex];

    if (prevImage) {
      setSelectedId(prevImage.id);
    }
  }, [images, currentIndex]);

  // Open modal
  const openModal = useCallback(() => {
    if (!images.length) return;

    setIsOpen(true);
  }, [images]);

  // Close modal
  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Select image by index
  const selectIndex = useCallback(
    (index) => {
      if (!images[index]) return;

      setSelectedId(images[index].id);
    },
    [images]
  );

  // Preload previous & next images
  useEffect(() => {
    if (
      images.length < 2 ||
      currentIndex < 0
    ) {
      return;
    }

    const indexes = [
      (currentIndex + 1) % images.length,
      (currentIndex - 1 + images.length) % images.length,
    ];

    indexes.forEach((index) => {
      const img = new Image();

      img.src = images[index].url;
    });
  }, [images, currentIndex]);

  return {
    images,

    selected,

    currentIndex,

    isOpen,

    selectImage,

    selectIndex,

    next,

    previous,

    openModal,

    closeModal,
  };
}